/**
 * Resident Entity Service - Request Management
 *
 * Handles resident service requests including:
 * - Creating and managing service requests
 * - Viewing request status and history
 * - Communication with vendors and managers
 * - Profile and preference management
 *
 * ============================================================
 * BACKEND INTEGRATION NOTES
 * ============================================================
 *
 * This service is ALREADY BACKEND-READY with proper API integration.
 *
 * CURRENT BEHAVIOR (Development Mode):
 * - When VITE_USE_MOCK_API=true OR no VITE_API_BASE_URL is set:
 *   → Falls back to getMockResidentRequests() for demo/development
 * - When backend is unavailable (network error, 404, etc.):
 *   → Gracefully falls back to mock data with console warning
 *
 * PRODUCTION SETUP:
 * 1. Set environment variables in .env.production:
 *    VITE_API_BASE_URL=https://your-api.example.com
 *    VITE_USE_MOCK_API=false
 *
 * 2. Ensure backend endpoints are implemented:
 *    POST   /api/resident/requests          → createRequest()
 *    GET    /api/resident/requests?filters  → getResidentRequests()
 *    GET    /api/resident/requests/:id      → getRequestById()
 *    PATCH  /api/resident/requests/:id      → updateRequest()
 *
 * For complete backend integration guide:
 * - docs/entities/resident/features/service-requests/SERVICE_REQUEST_BACKEND_INTEGRATION.md
 * - docs/entities/resident/features/service-requests/service-request-quick-reference.md
 *    POST   /api/resident/requests/:id/cancel → cancelRequest()
 *
 * 3. Backend must return ServiceRequest type (see types/shared/serviceRequest.ts)
 *
 * 4. Authentication: Token stored in localStorage as 'token'
 *    - All requests include: Authorization: Bearer ${token}
 *
 * NO CODE CHANGES NEEDED when going to production!
 * Just configure environment variables and ensure backend matches API contract.
 *
 * 📚 DOCUMENTATION:
 * Full guide: docs/features/marketplace/SERVICE_REQUEST_BACKEND_INTEGRATION.md
 * Quick ref: docs/features/marketplace/service-request-quick-reference.md
 *
 * ============================================================
 */

import {
  ServiceRequest,
  ServiceRequestCategory,
  ServiceRequestPriority,
  ServiceRequestStatus,
  ServiceRequestFilters,
} from '@/types/shared/serviceRequest';

import { ResidentProfile } from '@/types/entities/resident/profile';

// ====== RESIDENT REQUEST INTERFACES ======

export interface ResidentCreateRequestData {
  title: string;
  description: string;
  category: ServiceRequestCategory;
  priority: ServiceRequestPriority;
  location?: {
    building?: string;
    unit?: string;
    commonArea?: string;
    specificLocation?: string;
  };
  budget?: {
    min?: number;
    max?: number;
    currency: string;
  };
  timeline?: {
    preferredStartDate?: string;
    deadline?: string;
    estimatedDuration?: string;
    timeRestrictions?: {
      availableDays: string[];
      availableHours: {
        start: string;
        end: string;
      };
      excludedDates: string[];
    };
  };
  preferences?: {
    preferredVendors?: string[];
    excludedVendors?: string[];
    requiresCertification?: string[];
    accessRequirements?: {
      keyAccess: boolean;
      escortRequired: boolean;
      petsPresent: boolean;
      specialInstructions?: string;
    };
    communicationPreferences?: {
      method: 'email' | 'phone' | 'app' | 'text';
      frequency: 'minimal' | 'regular' | 'frequent';
      language?: string;
    };
  };
  attachments?: File[];
}

export interface ResidentRequestFilters {
  status?: ServiceRequestStatus[];
  category?: ServiceRequestCategory[];
  priority?: ServiceRequestPriority[];
  dateRange?: {
    start: string;
    end: string;
  };
  location?: {
    building?: string;
    unit?: string;
  };
  assignedVendor?: string;
}

export interface ResidentFeedbackData {
  requestId: string;
  rating: number; // 1-5 stars
  serviceQuality: number;
  communication: number;
  timeliness: number;
  cleanliness: number;
  professionalism: number;
  overallSatisfaction: number;
  comments?: string;
  wouldRecommend: boolean;
  followUpNeeded: boolean;
  followUpReason?: string;
  photos?: File[];
}

// ====== RESIDENT REQUEST SERVICE ======

class ResidentRequestService {
  private baseUrl = '/api/resident/requests';
  private profileUrl = '/api/resident/profile';
  private useMockApi = import.meta.env.VITE_USE_MOCK_API === 'true';
  private isDevelopment = import.meta.env.DEV || import.meta.env.NODE_ENV === 'development';

  // ====== REQUEST MANAGEMENT ======

  /**
   * Get resident's service requests
   */
  async getResidentRequests(
    residentId: string,
    filters?: ServiceRequestFilters,
  ): Promise<ServiceRequest[]> {
    // In development mode without backend, return mock data directly
    if (this.useMockApi || (!import.meta.env.VITE_API_BASE_URL && this.isDevelopment)) {
      return this.getMockResidentRequests();
    }

    try {
      const queryParams = new URLSearchParams();
      queryParams.append('residentId', residentId);

      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              value.forEach((v) => queryParams.append(`${key}[]`, v.toString()));
            } else if (typeof value === 'object') {
              queryParams.append(key, JSON.stringify(value));
            } else {
              queryParams.append(key, value.toString());
            }
          }
        });
      }

      const url = `${this.baseUrl}?${queryParams.toString()}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        // If response is not ok, fall back to mock data immediately
        console.warn(`API request failed with status ${response.status}, using mock data`);
        return this.getMockResidentRequests();
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.warn('API returned non-JSON response, using mock data');
        return this.getMockResidentRequests();
      }

      const data = await response.json();
      return data;
    } catch (error) {
      // Only log the error once, don't log the full stack trace
      if (error instanceof SyntaxError && error.message.includes('Unexpected token')) {
        console.warn('API endpoint not available, using mock data');
      } else {
        console.error('Error fetching resident requests:', error);
      }
      // Always return mock data on any error
      return this.getMockResidentRequests();
    }
  }

  /**
   * Get specific request by ID
   */
  async getRequestById(id: string): Promise<ServiceRequest> {
    try {
      const response = await fetch(`${this.baseUrl}/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch request: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching request:', error);
      const mockRequests = this.getMockResidentRequests();
      const request = mockRequests.find((r) => r.id === id);
      if (!request) {
        throw new Error('Request not found', { cause: error });
      }
      return request;
    }
  }

  /**
   * Create new service request
   */
  async createRequest(data: ResidentCreateRequestData): Promise<ServiceRequest> {
    try {
      const formData = new FormData();

      // Add basic data
      formData.append('title', data.title);
      formData.append('description', data.description);
      formData.append('category', data.category);
      formData.append('priority', data.priority);

      if (data.location) {
        formData.append('location', JSON.stringify(data.location));
      }

      if (data.budget) {
        formData.append('budget', JSON.stringify(data.budget));
      }

      if (data.timeline) {
        formData.append('timeline', JSON.stringify(data.timeline));
      }

      if (data.preferences) {
        formData.append('preferences', JSON.stringify(data.preferences));
      }

      // Add file attachments
      if (data.attachments) {
        data.attachments.forEach((file, index) => {
          formData.append(`attachments[${index}]`, file);
        });
      }

      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Failed to create request: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating request:', error);
      throw error;
    }
  }

  /**
   * Update existing request
   */
  async updateRequest(
    id: string,
    data: Partial<ResidentCreateRequestData>,
  ): Promise<ServiceRequest> {
    try {
      const formData = new FormData();

      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (key === 'attachments' && Array.isArray(value)) {
            value.forEach((file, index) => {
              formData.append(`attachments[${index}]`, file);
            });
          } else if (typeof value === 'object') {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, value.toString());
          }
        }
      });

      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Failed to update request: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating request:', error);
      throw error;
    }
  }

  /**
   * Cancel/withdraw request
   */
  async cancelRequest(id: string, reason?: string): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/${id}/cancel`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reason }),
      });

      if (!response.ok) {
        throw new Error(`Failed to cancel request: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error canceling request:', error);
      throw error;
    }
  }

  // ====== FEEDBACK AND RATINGS ======

  /**
   * Submit feedback for completed request
   */
  async submitFeedback(data: ResidentFeedbackData): Promise<void> {
    try {
      const formData = new FormData();

      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (key === 'photos' && Array.isArray(value)) {
            value.forEach((photo, index) => {
              formData.append(`photos[${index}]`, photo);
            });
          } else if (typeof value === 'object') {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, value.toString());
          }
        }
      });

      const response = await fetch(`${this.baseUrl}/${data.requestId}/feedback`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Failed to submit feedback: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      throw error;
    }
  }

  // ====== COMMUNICATION ======

  /**
   * Send message about a request
   */
  async sendMessage(requestId: string, message: string, attachments?: File[]): Promise<void> {
    try {
      const formData = new FormData();
      formData.append('message', message);

      if (attachments) {
        attachments.forEach((file, index) => {
          formData.append(`attachments[${index}]`, file);
        });
      }

      const response = await fetch(`${this.baseUrl}/${requestId}/messages`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Failed to send message: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  // ====== VENDOR SELECTION ======

  /**
   * Accept vendor proposal
   */
  async acceptVendorProposal(requestId: string, vendorId: string): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/${requestId}/accept-vendor`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ vendorId }),
      });

      if (!response.ok) {
        throw new Error(`Failed to accept vendor: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error accepting vendor:', error);
      throw error;
    }
  }

  /**
   * Decline vendor proposal
   */
  async declineVendorProposal(requestId: string, vendorId: string, reason?: string): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/${requestId}/decline-vendor`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ vendorId, reason }),
      });

      if (!response.ok) {
        throw new Error(`Failed to decline vendor: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error declining vendor:', error);
      throw error;
    }
  }

  // ====== FILE MANAGEMENT ======

  /**
   * Upload files for requests
   */
  async uploadFile(
    file: File,
    context?: 'request' | 'feedback' | 'message',
  ): Promise<{ url: string; id: string }> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      if (context) {
        formData.append('context', context);
      }

      const response = await fetch('/api/resident/upload', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Failed to upload file: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  }

  // ====== MOCK DATA FOR DEVELOPMENT ======

  private getMockResidentRequests(): ServiceRequest[] {
    return [
      {
        id: 'rr-001',
        title: 'Refrigerator Not Cooling',
        description: 'My refrigerator stopped cooling properly yesterday. Food is spoiling.',
        category: 'appliance_repair',
        priority: 'urgent',
        status: 'submitted',
        requestedBy: {
          id: '12345',
          name: 'John Smith',
          role: 'resident',
          unitNumber: '302',
          building: 'Building A',
          contactInfo: {
            phone: '555-0123',
            preferredMethod: 'phone',
          },
        },
        location: {
          building: 'Building A',
          unit: '302',
          room: 'Kitchen',
          specificLocation: 'Main refrigerator',
          accessInstructions: 'Please call 30 minutes before arrival',
        },
        budget: {
          max: 300,
          currency: 'USD',
        },
        timeline: {
          preferredStartDate: new Date(Date.now() + 24 * 60 * 60 * 1000)
            .toISOString()
            .split('T')[0],
          flexibleScheduling: true,
          timeRestrictions: {
            availableDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            availableHours: {
              start: '09:00',
              end: '17:00',
            },
            excludedDates: [],
          },
        },
        requirements: {
          insuranceRequired: true,
          bonded: true,
          backgroundCheck: true,
          minimumRating: 4.0,
        },
        attachments: [],
        responses: [],
        createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        updatedAt: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
      },
      {
        id: 'rr-002',
        title: 'Bathroom Faucet Leak',
        description: "Master bathroom faucet has a persistent drip that's getting worse.",
        category: 'plumbing',
        priority: 'medium',
        status: 'assigned',
        requestedBy: {
          id: '12345',
          name: 'John Smith',
          role: 'resident',
          unitNumber: '302',
          building: 'Building A',
          contactInfo: {
            email: 'user001@email.com',
            preferredMethod: 'email',
          },
        },
        location: {
          building: 'Building A',
          unit: '302',
          room: 'Master bathroom',
          specificLocation: 'Sink faucet',
          accessInstructions: 'Key available with building manager',
        },
        budget: {
          max: 200,
          currency: 'USD',
        },
        timeline: {
          preferredStartDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split('T')[0],
          flexibleScheduling: true,
          timeRestrictions: {
            availableDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            availableHours: {
              start: '10:00',
              end: '16:00',
            },
            excludedDates: [],
          },
        },
        requirements: {
          insuranceRequired: true,
          bonded: true,
          backgroundCheck: true,
          minimumRating: 4.5,
        },
        attachments: [],
        responses: [],
        assignment: {
          assignedVendorId: 'vendor-002',
          assignedVendorName: 'Reliable Plumbing Co.',
          assignedAt: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
          estimatedCost: 125,
          estimatedDuration: '1.5 hours',
          scheduledDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        },
        createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
        updatedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      },
    ];
  }
}

export const residentRequestService = new ResidentRequestService();
